import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, Plus, CheckCircle2, MapPin, Building2, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectSearchModal({ isOpen, onClose, onAdd, existingIds = [] }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`${API}/zoho/search-projects`, { params: { q: q.trim(), per_page: 25 } });
      const projects = (res.data.projects || []).map(p => ({
        id: p.id,
        name: p.deal_name || p.barbour_project_name || '(Unnamed)',
        stage: p.stage,
        postcode: p.postcode,
        barbour_id: p.barbour_project_id,
        value: p.barbour_value,
      }));
      setResults(projects);
    } catch (e) {
      toast.error('Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 400);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      await onAdd([...selected]);
      setSelected(new Set());
      setQuery('');
      setResults([]);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Add Projects to Tracking List</h2>
            <p className="text-[12px] text-gray-500 mt-0.5">Search by project name, postcode, or Barbour ABI number</p>
          </div>
          <div className="flex items-center gap-3">
            {selected.size > 0 && (
              <button
                onClick={handleAdd}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
                data-testid="pt-search-add-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Add {selected.size} project{selected.size !== 1 ? 's' : ''}
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Search box */}
        <div className="px-6 pt-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Penryn Street, SW1A 1AA, or 12345678"
              className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
              data-testid="pt-project-search-input"
            />
            {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 animate-spin" />}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          {results.length === 0 && query.trim() && !loading && (
            <div className="text-center py-10 text-gray-400 text-sm">No projects found for &ldquo;{query}&rdquo;</div>
          )}
          {results.length === 0 && !query.trim() && (
            <div className="text-center py-10 text-gray-300 text-sm">Start typing to search CRM projects</div>
          )}
          <div className="space-y-1.5 pt-1">
            {results.map(proj => {
              const isExisting = existingIds.includes(proj.id);
              const isSelected = selected.has(proj.id);
              return (
                <button
                  key={proj.id}
                  onClick={() => !isExisting && toggle(proj.id)}
                  disabled={isExisting}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-all flex items-start gap-3 ${
                    isExisting
                      ? 'bg-gray-50 border-gray-100 opacity-60 cursor-not-allowed'
                      : isSelected
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-white border-gray-100 hover:border-blue-200 hover:bg-blue-50/30'
                  }`}
                  data-testid={`pt-search-result-${proj.id}`}
                >
                  <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    isExisting ? 'border-gray-300 bg-gray-100' : isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                  }`}>
                    {(isSelected || isExisting) && (
                      <CheckCircle2 className={`w-4 h-4 ${isExisting ? 'text-gray-400' : 'text-white'}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">{proj.name}</div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {proj.stage && (
                        <span className="text-[11px] text-gray-500 flex items-center gap-1">
                          <Building2 className="w-3 h-3" />{proj.stage}
                        </span>
                      )}
                      {proj.postcode && (
                        <span className="text-[11px] text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{proj.postcode}
                        </span>
                      )}
                      {proj.barbour_id && (
                        <span className="text-[11px] text-gray-400">ID: {proj.barbour_id}</span>
                      )}
                      {isExisting && <span className="text-[11px] text-blue-500 font-medium">Already in list</span>}
                    </div>
                  </div>
                  {proj.value && (
                    <div className="text-xs font-semibold text-gray-600 flex-shrink-0">
                      £{Number(proj.value).toLocaleString()}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-3 flex items-center justify-between">
          <span className="text-[12px] text-gray-400">
            {selected.size > 0 ? `${selected.size} selected` : 'Click a result to select it'}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => { setSelected(new Set()); setQuery(''); setResults([]); onClose(); }}
              className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            {selected.size > 0 && (
              <button
                onClick={handleAdd}
                disabled={saving}
                className="px-5 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {saving ? 'Adding…' : `Add ${selected.size} to List`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
